/**
 * Background Location Service
 * 
 * This service provides GPS tracking that works in the background on native mobile apps.
 * On web browsers, it falls back to the standard Geolocation API (which stops when backgrounded).
 * 
 * For true background tracking, the app must be built with Capacitor for iOS/Android.
 */

import { Capacitor } from '@capacitor/core';
import { registerPlugin } from '@capacitor/core';

// Register the background geolocation plugin
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

// Check if we're running on a native platform
const isNative = Capacitor.isNativePlatform();

// Configuration for GPS tracking
const GPS_CONFIG = {
  // High accuracy for delivery tracking
  backgroundMessage: 'Local Flyer is tracking your deliveries',
  backgroundTitle: 'Delivery Tracking Active',
  requestPermissions: true,
  stale: false,
  distanceFilter: 5, // Minimum distance (meters) before triggering update
};

// Web fallback configuration
const WEB_GPS_CONFIG = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 5000,
};

class BackgroundLocationService {
  constructor() {
    this.watcherId = null;
    this.webWatchId = null;
    this.pingInterval = null;
    this.callbacks = {
      onLocation: null,
      onError: null,
    };
    this.isTracking = false;
    this.lastPosition = null;
  }

  /**
   * Check if background location tracking is available
   * Returns true on native platforms, false on web
   */
  isBackgroundSupported() {
    return isNative;
  }

  /**
   * Get the platform name
   */
  getPlatform() {
    return Capacitor.getPlatform();
  }

  /**
   * Request location permissions
   * Returns a promise that resolves to the permission status
   */
  async requestPermissions() {
    if (isNative) {
      try {
        // On native, permissions are requested when starting the watcher
        // This is a placeholder for explicit permission requests if needed
        return { status: 'prompt' };
      } catch (error) {
        console.error('Error requesting permissions:', error);
        return { status: 'denied' };
      }
    } else {
      // Web: Check permission status
      if (navigator.permissions) {
        try {
          const result = await navigator.permissions.query({ name: 'geolocation' });
          return { status: result.state };
        } catch {
          return { status: 'unknown' };
        }
      }
      return { status: 'unknown' };
    }
  }

  /**
   * Start tracking location
   * 
   * @param {Function} onLocation - Callback for location updates: (position) => void
   *   position: { latitude, longitude, accuracy, altitude, speed, heading, timestamp }
   * @param {Function} onError - Callback for errors: (error) => void
   * @returns {Promise<boolean>} - True if tracking started successfully
   */
  async startTracking(onLocation, onError) {
    if (this.isTracking) {
      console.warn('Location tracking is already active');
      return true;
    }

    this.callbacks.onLocation = onLocation;
    this.callbacks.onError = onError;

    if (isNative) {
      return this._startNativeTracking();
    } else {
      return this._startWebTracking();
    }
  }

  /**
   * Stop tracking location
   */
  async stopTracking() {
    if (!this.isTracking) {
      return;
    }

    if (isNative) {
      await this._stopNativeTracking();
    } else {
      this._stopWebTracking();
    }

    this.isTracking = false;
    this.callbacks.onLocation = null;
    this.callbacks.onError = null;
  }

  /**
   * Get the last known position
   */
  getLastPosition() {
    return this.lastPosition;
  }

  // ==================== NATIVE TRACKING ====================

  async _startNativeTracking() {
    try {
      console.log('[BackgroundLocation] Starting native background tracking...');
      
      this.watcherId = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: GPS_CONFIG.backgroundMessage,
          backgroundTitle: GPS_CONFIG.backgroundTitle,
          requestPermissions: GPS_CONFIG.requestPermissions,
          stale: GPS_CONFIG.stale,
          distanceFilter: GPS_CONFIG.distanceFilter,
        },
        (location, error) => {
          if (error) {
            console.error('[BackgroundLocation] Native error:', error);
            if (this.callbacks.onError) {
              this.callbacks.onError({
                code: error.code || 0,
                message: error.message || 'Unknown error',
              });
            }
            return;
          }

          if (location) {
            const position = {
              latitude: location.latitude,
              longitude: location.longitude,
              accuracy: location.accuracy,
              altitude: location.altitude,
              speed: location.speed,
              heading: location.bearing,
              timestamp: location.time,
            };
            
            this.lastPosition = position;
            
            if (this.callbacks.onLocation) {
              this.callbacks.onLocation(position);
            }
          }
        }
      );

      this.isTracking = true;
      console.log('[BackgroundLocation] Native tracking started, watcher ID:', this.watcherId);
      return true;
    } catch (error) {
      console.error('[BackgroundLocation] Failed to start native tracking:', error);
      if (this.callbacks.onError) {
        this.callbacks.onError({
          code: -1,
          message: error.message || 'Failed to start background location',
        });
      }
      return false;
    }
  }

  async _stopNativeTracking() {
    if (this.watcherId) {
      try {
        await BackgroundGeolocation.removeWatcher({ id: this.watcherId });
        console.log('[BackgroundLocation] Native tracking stopped');
      } catch (error) {
        console.error('[BackgroundLocation] Error stopping native tracking:', error);
      }
      this.watcherId = null;
    }
  }

  // ==================== WEB TRACKING ====================

  _startWebTracking() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.error('[BackgroundLocation] Geolocation not supported');
        if (this.callbacks.onError) {
          this.callbacks.onError({
            code: -1,
            message: 'Geolocation is not supported by this browser',
          });
        }
        resolve(false);
        return;
      }

      console.log('[BackgroundLocation] Starting web tracking (foreground only)...');

      // Start watching position
      this.webWatchId = navigator.geolocation.watchPosition(
        (position) => this._handleWebPosition(position),
        (error) => this._handleWebError(error),
        WEB_GPS_CONFIG
      );

      // Backup ping every 5-10 seconds (as requested by user)
      this.pingInterval = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => this._handleWebPosition(position),
          () => {}, // Ignore ping errors
          WEB_GPS_CONFIG
        );
      }, 5000); // 5 second intervals for high accuracy

      this.isTracking = true;
      console.log('[BackgroundLocation] Web tracking started');
      resolve(true);
    });
  }

  _stopWebTracking() {
    if (this.webWatchId !== null) {
      navigator.geolocation.clearWatch(this.webWatchId);
      this.webWatchId = null;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    console.log('[BackgroundLocation] Web tracking stopped');
  }

  _handleWebPosition(geolocationPosition) {
    const position = {
      latitude: geolocationPosition.coords.latitude,
      longitude: geolocationPosition.coords.longitude,
      accuracy: geolocationPosition.coords.accuracy,
      altitude: geolocationPosition.coords.altitude,
      speed: geolocationPosition.coords.speed,
      heading: geolocationPosition.coords.heading,
      timestamp: geolocationPosition.timestamp,
    };

    this.lastPosition = position;

    if (this.callbacks.onLocation) {
      this.callbacks.onLocation(position);
    }
  }

  _handleWebError(error) {
    console.error('[BackgroundLocation] Web error:', error);
    if (this.callbacks.onError) {
      this.callbacks.onError({
        code: error.code,
        message: error.message,
      });
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Open location settings (native only)
   * Useful for directing users to enable location services
   */
  async openSettings() {
    if (isNative) {
      try {
        await BackgroundGeolocation.openSettings();
      } catch (error) {
        console.error('[BackgroundLocation] Failed to open settings:', error);
      }
    } else {
      console.warn('[BackgroundLocation] Cannot open settings on web platform');
    }
  }
}

// Export a singleton instance
const backgroundLocationService = new BackgroundLocationService();
export default backgroundLocationService;

// Also export the class for testing
export { BackgroundLocationService };
